import { useTranslation } from 'react-i18next'
import type { BaseFilterValue, FilterSection, Filter } from '../types'
import { useMemo } from 'react'

export const useFilterSections = (facets: {
  [key: string]: BaseFilterValue[]
}): {
  filterSections: FilterSection[]
} => {
  const { t } = useTranslation()

  const filterSections: FilterSection[] = useMemo(
    () =>
      [
        {
          title: t('general.discount'),
          filters: [
            {
              title: t('general.discount'),
              titleHidden: true,
              type: 'choicechips',
              name: 'price_ratio',
              values:
                facets['price_ratio']
                  ?.filter((ratio) => {
                    const ratioValue = parseFloat(ratio.value)
                    return ratioValue > 0 && ratioValue < 1
                  })
                  ?.map(({ value, label }) => {
                    const discount = Math.round((1 - parseFloat(value)) * 100)
                    const roundedDiscount = discount - (discount % 5)
                    const displayLabel = `${roundedDiscount}%` ?? label
                    return { label: displayLabel, value }
                  })
                  ?.reduce<{ label: string; value: string }[]>(
                    (acc, { label, value }) => {
                      const existing = acc.find((item) => item.label === label)
                      if (existing) {
                        existing.value = `${existing.value},${value}`
                      } else {
                        acc.push({ label, value })
                      }
                      return acc
                    },
                    [],
                  )
                  .sort((a, b) => parseFloat(b.value) - parseFloat(a.value)) ??
                [],
            },
          ] as Filter<'choicechips', BaseFilterValue>[],
        },
        {
          title: t('general.category'),
          filters: [
            {
              title: 'category',
              titleHidden: true,
              type: 'checklist',
              name: 'category',
              values: facets['meta.lounge.category'] ?? [],
            },
          ] as Filter<'checklist', BaseFilterValue>[],
        },
        {
          title: t('general.categories'),
          filters: [
            {
              title: 'categories',
              titleHidden: true,
              type: 'checklist',
              name: 'categories',
              values: facets['meta.lounge.categories'] ?? [],
            },
          ] as Filter<'checklist', BaseFilterValue>[],
        },
        {
          title: t('general.color'),
          filters: [
            {
              title: t('general.color'),
              titleHidden: true,
              type: 'swatches',
              name: 'color',
              values: [
                {
                  label: t('color.black'),
                  value: 'black',
                  color: '#000',
                },
                {
                  label: t('color.white'),
                  value: 'white',
                  color: '#EBE8EA',
                },
                {
                  label: t('color.cream'),
                  value: 'cream',
                  color: '#EAE6E3',
                },
                {
                  label: t('color.grey'),
                  value: 'grey',
                  color: '#C8C8C8',
                },
                {
                  label: t('color.brown'),
                  value: 'Brown',
                  color: '#88594E',
                },
                {
                  label: t('color.orange'),
                  value: 'orange',
                  color: '#F5BB6A',
                },
                {
                  label: t('color.purple'),
                  value: 'purple',
                  color: '#C285E0',
                },
                {
                  label: t('color.pink'),
                  value: 'pink',
                  color: '#EC6190',
                },
                { label: t('color.red'), value: 'red', color: '#B4321A' },
                {
                  label: t('color.blue'),
                  value: 'blue',
                  color: '#2F74B7',
                },
                {
                  label: t('color.yellow'),
                  value: 'yellow',
                  color: '#F0E49C',
                },
                {
                  label: t('color.green'),
                  value: 'green',
                  color: '#205005',
                },
              ],
            },
          ] as Filter<
            'swatches',
            BaseFilterValue & { color: string; checkColor?: string }
          >[],
        },
        {
          title: t('general.bra_size'),
          filters: [
            {
              title: t('general.band_size'),
              titleHidden: false,
              type: 'choicechips',
              name: 'band_size',
              values: facets['options.band_size'] ?? [],
            },
            {
              title: t('general.cup_size'),
              titleHidden: false,
              type: 'choicechips',
              name: 'cup_size',
              values: facets['options.cup_size'] ?? [],
            },
          ] as Filter<'choicechips', BaseFilterValue>[],
        },
        {
          title: t('general.size'),
          filters: [
            {
              title: t('general.size'),
              titleHidden: true,
              type: 'checklist',
              name: 'size',
              values: facets['options.size'] ?? [],
            },
          ] as Filter<'checklist', BaseFilterValue>[],
        },
      ].filter((section) =>
        section.filters.some((filter) => filter.values.length > 0),
      ),
    [facets],
  )

  return { filterSections }
}
